'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BaseDefiYieldOverviewResponse } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';

import { TrendChart, type ChartDataPoint } from '../../shared/trend-chart';
import { buildHistoryChartData } from './chart-data';
import { EstAprTooltipContent } from './est-apr-tooltip-content';

interface AprModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  overview: BaseDefiYieldOverviewResponse;
}

export const AprModal = ({ isOpen, onClose, onExit, overview }: AprModalProps) => {
  const todayApr = Number(overview.estimateApr);
  const chartData = useMemo<ChartDataPoint[]>(
    () => buildHistoryChartData(overview.estimateAprHistory, (item) => Number(item.estimateApr), 7, todayApr),
    [overview.estimateAprHistory, todayApr],
  );

  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null);

  const displayValue = hoveredPoint?.value ?? todayApr;

  const formatYAxisLabel = useCallback((value: number) => `${(value * 100).toFixed(2)}%`, []);

  // trend chart 에서 use-svg-chart-dimensions 에서 사용하는 ResizeObserver 가 동작하지 않아서, 200ms 지연 후 마운트 (원인: 모달 애니메이션과 충돌)
  const [shouldMountChart, setShouldMountChart] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setShouldMountChart(false);
      return;
    }
    const timeoutId = setTimeout(() => setShouldMountChart(true), 200);
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Common.r251209.Apr' />} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          <div className={css({ color: 'content.default.level-2' })}>
            <EstAprTooltipContent />
          </div>

          <div
            className={stack({
              gap: 16,
              p: 16,
              rounded: 12,
              bg: 'background.default.level-1',
            })}
          >
            <div className={hstack({ justify: 'space-between' })}>
              <div className={stack({ gap: 4 })}>
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                  <FormattedMessage id='Common.r251209.Apr' /> (7D)
                </p>
                <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
                  <Formatted.Percent value={displayValue} isRate precision={2} toFixed />
                </p>
              </div>
              <p
                className={center({
                  direction: 'row',
                  py: 8,
                  px: 12,
                  rounded: 1000,
                  bg: 'background.accent.secondary.tint',
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-5',
                })}
              >
                <FormattedMessage id='Common.r251107.days_days' values={{ days: 7 }} />
              </p>
            </div>
            <div className={css({ h: 204 })}>
              {shouldMountChart && (
                <TrendChart data={chartData} onHover={setHoveredPoint} formatYAxisLabel={formatYAxisLabel} />
              )}
            </div>
            <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', textAlign: 'right' })}>
              <FormattedMessage
                id='Common.r251107.Latest_update_colon_date_time'
                values={{
                  dateTime: (
                    <Datetime
                      key='dateTime'
                      value={overview.estimateAprLatestUpdate}
                      format='yyyy.MM.dd, HH:mm'
                      isUTC
                      withUTCText
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
